import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Typography,
  Popover,
  Checkbox,
  message,
  Row,
  Col,
  Spin,
  Empty,
  Select,
  Upload,
  Modal
} from 'antd';
import {
  SearchOutlined,
  ColumnHeightOutlined,
  EditOutlined,
  SaveOutlined,
  ClearOutlined,
  ProjectOutlined,
  DownloadOutlined,
  UploadOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { translationsService } from '../../api/translations';
import { translationProjectsService } from '../../api/translationProjects';
import { aiTranslationService } from '../../api/aiTranslation';
import AIIcon from '../../components/translations/AIIcon';
import BulkTranslateModal from '../../components/translations/BulkTranslateModal';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { TextArea } = Input;

// AI Translation Limits
const MAX_AI_TRANSLATION_KEYS = 2000; // Максимум ключей для одного AI перевода

interface TranslationKey {
  key: string;
  translations: Record<string, string>;
}

interface EditableTranslation {
  languageId: string;
  key: string;
  value: string;
}

interface TranslationResult {
  key: string;
  languageId: string;
  languageName: string;
  languageCode: string;
  originalValue?: string;
  translatedValue: string;
  success: boolean;
  error?: string;
}

const TranslationsManager: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [visibleLanguages, setVisibleLanguages] = useState<string[]>([]);
  const [editingCell, setEditingCell] = useState<{
    key: string;
    languageId: string;
  } | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [allTranslationKeys, setAllTranslationKeys] = useState<Set<string>>(new Set());
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  
  // AI Translation states
  const [aiTranslateModalVisible, setAiTranslateModalVisible] = useState(false);
  const [aiTranslateResults, setAiTranslateResults] = useState<TranslationResult[]>([]);
  const [aiTranslateLoading, setAiTranslateLoading] = useState(false);
  const [totalKeysToTranslate, setTotalKeysToTranslate] = useState(0);
  const [currentTranslatingText, setCurrentTranslatingText] = useState('');
  const [processedCount, setProcessedCount] = useState(0);

  // Fetch all projects
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['translationProjects'],
    queryFn: () => translationProjectsService.getProjects(),
  });

  // Fetch all active languages
  const { data: languagesData, isLoading: languagesLoading } = useQuery({
    queryKey: ['languages'],
    queryFn: () => translationsService.getLanguages({ limit: 50 }),
  });

  // Fetch project translations when project is selected
  const { data: projectTranslationsData, isLoading: translationsLoading } = useQuery({
    queryKey: ['projectTranslations', selectedProjectId],
    queryFn: () => translationProjectsService.getProjectTranslations(selectedProjectId!),
    enabled: !!selectedProjectId,
  });

  const activeLanguages = useMemo(() => {
    return languagesData?.items?.filter(lang => lang.isActive) || [];
  }, [languagesData]);

  // Debounce search text to avoid excessive filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Initialize visible languages from localStorage or activeLanguages
  useEffect(() => {
    if (activeLanguages.length > 0 && visibleLanguages.length === 0) {
      const savedLanguages = localStorage.getItem('translationsManager_visibleLanguages');
      if (savedLanguages) {
        try {
          const parsed = JSON.parse(savedLanguages);
          // Filter to only include languages that are still active
          const validLanguages = parsed.filter((id: string) => 
            activeLanguages.some(lang => lang.id === id)
          );
          if (validLanguages.length > 0) {
            setVisibleLanguages(validLanguages);
            return;
          }
        } catch (e) {
          console.error('Error parsing saved visible languages:', e);
        }
      }
      // Default: show all active languages
      setVisibleLanguages(activeLanguages.map(lang => lang.id));
    }
  }, [activeLanguages, visibleLanguages.length]);

  // Save visible languages to localStorage when they change
  useEffect(() => {
    if (visibleLanguages.length > 0) {
      localStorage.setItem('translationsManager_visibleLanguages', JSON.stringify(visibleLanguages));
    }
  }, [visibleLanguages]);

  // Auto-select first project if none selected
  useEffect(() => {
    if (projectsData && projectsData.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projectsData[0].id);
    }
  }, [projectsData, selectedProjectId]);

  // Clear row selection when project changes
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [selectedProjectId]);

  // Process project translations data
  const translationKeysData = useMemo(() => {
    if (!projectTranslationsData || !projectTranslationsData.translations) {
      return [];
    }

    const keysMap = new Map<string, Record<string, string>>();
    
    // Create a mapping from language codes to language IDs
    const codeToIdMap = new Map<string, string>();
    activeLanguages.forEach(lang => {
      codeToIdMap.set(lang.code, lang.id);
    });
    
    // Process project translations: { translations: { "en": { "key1": "value1" }, "ru": { ... } } }
    Object.entries(projectTranslationsData.translations).forEach(([languageCode, translations]) => {
      if (translations && typeof translations === 'object') {
        // Use language ID from activeLanguages, fallback to code if not found
        const languageId = codeToIdMap.get(languageCode) || languageCode;
        
        Object.entries(translations).forEach(([key, value]) => {
          if (!key) return;
          
          if (!keysMap.has(key)) {
            keysMap.set(key, {});
          }
          keysMap.get(key)![languageId] = value || '';
        });
      }
    });

    return Array.from(keysMap.entries()).map(([key, translations]) => ({
      key,
      translations,
    }));
  }, [projectTranslationsData, activeLanguages]);

  // Update allTranslationKeys separately to avoid infinite re-renders
  const allKeysSet = useMemo(() => {
    return new Set(translationKeysData.filter(item => item && item.key).map(item => item.key));
  }, [translationKeysData]);

  useEffect(() => {
    setAllTranslationKeys(allKeysSet);
  }, [allKeysSet]);

  // Filter translations based on search with debouncing
  const filteredTranslations = useMemo(() => {
    if (!debouncedSearchText) return translationKeysData;
    
    const search = debouncedSearchText.toLowerCase();
    return translationKeysData.filter(item => {
      if (!item || !item.key || !item.translations) return false;
      
      const keyMatch = item.key.toLowerCase().includes(search);
      const valueMatch = Object.values(item.translations).some(value => 
        value?.toLowerCase().includes(search)
      );
      return keyMatch || valueMatch;
    });
  }, [translationKeysData, debouncedSearchText]);

  // Update translation mutation
  const updateTranslationMutation = useMutation({
    mutationFn: async ({ languageId, key, value }: EditableTranslation) => {
      if (!selectedProjectId) {
        throw new Error('No project selected');
      }
      
      try {
        // Try to update existing project translation first
        return await translationProjectsService.updateProjectTranslation(
          selectedProjectId,
          languageId,
          key,
          value
        );
      } catch (error) {
        console.log('Update failed, trying to create new translation:', error);
        // If update fails, try to create new translation
        return await translationProjectsService.createProjectTranslation(
          selectedProjectId,
          languageId,
          key,
          value
        );
      }
    },
    onSuccess: () => {
      message.success('Перевод сохранен');
      
      // Invalidate project translations query
      queryClient.invalidateQueries({ 
        queryKey: ['projectTranslations', selectedProjectId] 
      });
    },
    onError: (error) => {
      console.error('Translation mutation error:', error);
      message.error('Ошибка при сохранении перевода');
    },
  });

  // Bulk delete selected keys (across all languages)
  const bulkDeleteMutation = useMutation({
    mutationFn: async (keys: string[]) => {
      if (!selectedProjectId) {
        throw new Error('No project selected');
      }
      return translationProjectsService.bulkDeleteProjectKeys(selectedProjectId, keys);
    },
    onSuccess: (result) => {
      message.success(`Удалено ${result.deletedCount} переводов (${result.keysRequested} ключей, все языки)`);
      setSelectedRowKeys([]);
      queryClient.invalidateQueries({
        queryKey: ['projectTranslations', selectedProjectId]
      });
    },
    onError: (error) => {
      console.error('Bulk delete error:', error);
      message.error('Ошибка при удалении ключей');
    },
  });

  const handleBulkDelete = () => {
    const keys = selectedRowKeys.map(String);
    if (keys.length === 0) return;

    Modal.confirm({
      title: 'Удалить выбранные ключи?',
      content: (
        <div>
          <p>Будет безвозвратно удалено <b>{keys.length}</b> ключей <b>во всех языках</b> проекта.</p>
          <div style={{ maxHeight: 150, overflowY: 'auto', background: '#fafafa', padding: 8, borderRadius: 4 }}>
            {keys.slice(0, 50).map(key => (
              <div key={key}><Text code style={{ fontSize: '11px' }}>{key}</Text></div>
            ))}
            {keys.length > 50 && <Text type="secondary">... и еще {keys.length - 50}</Text>}
          </div>
        </div>
      ),
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: () => bulkDeleteMutation.mutateAsync(keys),
    });
  };

  // Rows the bulk actions operate on: selected keys, or everything filtered if nothing selected
  const getTranslationScope = () => {
    if (selectedRowKeys.length === 0) return filteredTranslations;
    const selectedSet = new Set(selectedRowKeys.map(String));
    return filteredTranslations.filter(item => item && selectedSet.has(item.key));
  };

  // Handle language visibility toggle
  const handleLanguageVisibilityChange = (languageId: string, visible: boolean) => {
    if (visible) {
      setVisibleLanguages(prev => [...prev, languageId]);
    } else {
      setVisibleLanguages(prev => prev.filter(id => id !== languageId));
    }
  };

  // Handle cell edit start
  const handleEditStart = (key: string, languageId: string, currentValue: string) => {
    setEditingCell({ key, languageId });
    setEditValues({ [`${key}-${languageId}`]: currentValue || '' });
  };

  // Handle cell edit save
  const handleEditSave = async (key: string, languageId: string) => {
    const editKey = `${key}-${languageId}`;
    const newValue = editValues[editKey];
    
    if (newValue === undefined) {
      return;
    }

    try {
      await updateTranslationMutation.mutateAsync({
        languageId,
        key,
        value: newValue,
      });
      setEditingCell(null);
      setEditValues(prev => {
        const { [editKey]: _, ...rest } = prev;
        void _; // ESLint ignore
        return rest;
      });
    } catch (error) {
      // Error is handled by mutation onError
    }
  };

  // Handle cell edit cancel
  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValues({});
  };

  // Export to Excel
  const handleExportToExcel = () => {
    if (!selectedProjectId || translationKeysData.length === 0) {
      message.warning('Нет данных для экспорта');
      return;
    }

    try {
      // Get all visible languages for columns
      const languageColumns = visibleLanguages.map(langId => {
        const lang = activeLanguages.find(l => l.id === langId);
        return lang ? { id: langId, name: lang.name, code: lang.code } : null;
      }).filter(Boolean) as { id: string; name: string; code: string }[];

      // Prepare data for Excel
      const excelData = translationKeysData.map(item => {
        const row: Record<string, string> = { 'Ключ': item.key };
        languageColumns.forEach(lang => {
          row[`${lang.name} (${lang.code})`] = item.translations[lang.id] || '';
        });
        return row;
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [{ wch: 40 }]; // Key column
      languageColumns.forEach(() => colWidths.push({ wch: 30 }));
      worksheet['!cols'] = colWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      const projectName = projectsData?.find(p => p.id === selectedProjectId)?.displayName || 'translations';
      XLSX.utils.book_append_sheet(workbook, worksheet, projectName.substring(0, 31)); // Sheet name max 31 chars

      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `${projectName}_${date}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
      message.success(`Экспортировано ${translationKeysData.length} ключей`);
    } catch (error) {
      console.error('Export error:', error);
      message.error('Ошибка при экспорте в Excel');
    }
  };

  // Import from Excel
  const handleImportFromExcel = (file: File) => {
    if (!selectedProjectId) {
      message.error('Сначала выберите проект');
      return false;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          message.error('Файл пустой или содержит только заголовки');
          return;
        }

        // Get headers (first row)
        const headers = jsonData[0] as unknown as string[];
        
        // Validate: must have exactly 2 columns
        if (headers.length !== 2) {
          Modal.error({
            title: 'Неверный формат файла',
            content: (
              <div>
                <p>Файл должен содержать ровно 2 колонки:</p>
                <ul>
                  <li><b>Колонка 1:</b> {`"ключ" или "key"`}</li>
                  <li><b>Колонка 2:</b> код языка (например: en, ru, tr, uz)</li>
                </ul>
                <p>Найдено колонок: {headers.length}</p>
              </div>
            ),
          });
          return;
        }

        // Validate first column name
        const keyColumnName = headers[0]?.toString().toLowerCase().trim();
        if (keyColumnName !== 'ключ' && keyColumnName !== 'key') {
          Modal.error({
            title: 'Неверный формат файла',
            content: (
              <div>
                <p>Первая колонка должна называться {`"ключ" или "key"`}</p>
                <p>Найдено: {`"${headers[0]}"`}</p>
              </div>
            ),
          });
          return;
        }

        // Validate second column - should be language code
        const languageCode = headers[1]?.toString().toLowerCase().trim();
        const targetLanguage = activeLanguages.find(lang => lang.code.toLowerCase() === languageCode);
        
        if (!targetLanguage) {
          Modal.error({
            title: 'Неверный код языка',
            content: (
              <div>
                <p>Код языка {`"${headers[1]}"`} не найден в системе.</p>
                <p>Доступные языки:</p>
                <ul>
                  {activeLanguages.map(lang => (
                    <li key={lang.id}><b>{lang.code}</b> - {lang.name}</li>
                  ))}
                </ul>
              </div>
            ),
          });
          return;
        }

        // Process data rows
        const rows = jsonData.slice(1) as unknown as string[][];
        const validRows = rows.filter(row => row[0] && row[1]); // Both key and value must exist

        if (validRows.length === 0) {
          message.error('Нет данных для импорта');
          return;
        }

        // Confirm import
        Modal.confirm({
          title: 'Подтверждение импорта',
          content: (
            <div>
              <p>Будет импортировано <b>{validRows.length}</b> переводов</p>
              <p>Язык: <b>{targetLanguage.name} ({targetLanguage.code})</b></p>
              <p>Существующие переводы будут перезаписаны!</p>
            </div>
          ),
          okText: 'Импортировать',
          cancelText: 'Отмена',
          onOk: async () => {
            const hideLoading = message.loading('Импорт переводов...', 0);
            let successCount = 0;
            let errorCount = 0;

            for (const row of validRows) {
              const key = row[0]?.toString().trim();
              const value = row[1]?.toString().trim();

              if (!key || !value) continue;

              try {
                await translationProjectsService.updateProjectTranslation(
                  selectedProjectId,
                  targetLanguage.id,
                  key,
                  value
                );
                successCount++;
              } catch {
                // Try to create if update fails
                try {
                  await translationProjectsService.createProjectTranslation(
                    selectedProjectId,
                    targetLanguage.id,
                    key,
                    value
                  );
                  successCount++;
                } catch {
                  errorCount++;
                }
              }
            }

            hideLoading();
            
            // Refresh data
            queryClient.invalidateQueries({ queryKey: ['projectTranslations', selectedProjectId] });
            
            if (errorCount === 0) {
              message.success(`Успешно импортировано ${successCount} переводов`);
            } else {
              message.warning(`Импортировано ${successCount} переводов, ошибок: ${errorCount}`);
            }
          },
        });
      } catch (error) {
        console.error('Import error:', error);
        message.error('Ошибка при чтении файла Excel');
      }
    };

    reader.readAsArrayBuffer(file);
    return false; // Prevent default upload behavior
  };

  // Handle AI Modal Open
  const handleAIBulkTranslate = () => {
    if (!selectedProjectId) {
      message.warning('Выберите проект для AI перевода');
      return;
    }
    
    // Find keys with missing translations (only selected keys if selection is active)
    const keysToTranslate: Array<{ key: string; languageId: string; languageName: string; languageCode: string }> = [];

    getTranslationScope().forEach(item => {
      if (!item || !item.key || !item.translations) return;
      
      visibleLanguages.forEach(languageId => {
        const language = activeLanguages.find(lang => lang.id === languageId);
        if (language && (!item.translations[languageId] || item.translations[languageId].trim() === '')) {
          keysToTranslate.push({
            key: item.key,
            languageId,
            languageName: language.name,
            languageCode: language.code
          });
        }
      });
    });

    if (keysToTranslate.length === 0) {
      message.info('Все переводы уже существуют для выбранных ключей');
      return;
    }

    // ОГРАНИЧЕНИЕ: Максимум ключей за раз
    const limitedKeysToTranslate = keysToTranslate.slice(0, MAX_AI_TRANSLATION_KEYS);
    
    if (keysToTranslate.length > MAX_AI_TRANSLATION_KEYS) {
      message.warning(`🚨 Обнаружено ${keysToTranslate.length} ключей, обработаем первые ${MAX_AI_TRANSLATION_KEYS} для оптимальной производительности`);
    }

    console.log(`🔍 ОТКРЫВАЕМ МОДАЛ: найдено ${keysToTranslate.length} отсутствующих переводов, обрабатываем ${limitedKeysToTranslate.length}`);
    
    // ПРАВИЛЬНО устанавливаем количество ДО открытия модала
    setTotalKeysToTranslate(limitedKeysToTranslate.length);
    setAiTranslateResults([]); // Clear previous results
    setProcessedCount(0);
    setCurrentTranslatingText('');
    setAiTranslateModalVisible(true);
  };

  // Handle AI Translation Process
  const handleStartAITranslation = async () => {
    if (aiTranslateLoading) return;
    
    setAiTranslateLoading(true);
    
    try {
      // Find keys with missing translations again (in case something changed)
      const allKeysToTranslate: Array<{ key: string; languageId: string; languageName: string; languageCode: string }> = [];

      getTranslationScope().forEach(item => {
        visibleLanguages.forEach(languageId => {
          const language = activeLanguages.find(lang => lang.id === languageId);
          if (language && (!item.translations[languageId] || item.translations[languageId].trim() === '')) {
            allKeysToTranslate.push({
              key: item.key,
              languageId,
              languageName: language.name,
              languageCode: language.code
            });
          }
        });
      });

      // ОГРАНИЧЕНИЕ: Максимум ключей за раз
      const keysToTranslate = allKeysToTranslate.slice(0, MAX_AI_TRANSLATION_KEYS);
      
      console.log(`🚀 НАЧИНАЕМ ПЕРЕВОД: найдено ${allKeysToTranslate.length}, обрабатываем ${keysToTranslate.length}`);
      
      // Обновляем totalKeysToTranslate если нужно
      if (keysToTranslate.length !== totalKeysToTranslate) {
        setTotalKeysToTranslate(keysToTranslate.length);
      }

      // Group by language for batch processing
      const groupedByLanguage = keysToTranslate.reduce((acc, item) => {
        if (!acc[item.languageCode]) {
          acc[item.languageCode] = [];
        }
        acc[item.languageCode].push(item);
        return acc;
      }, {} as Record<string, typeof keysToTranslate>);

      const allResults: TranslationResult[] = [];

      // Process each language separately
      for (const [languageCode, items] of Object.entries(groupedByLanguage)) {
        const language = activeLanguages.find(lang => lang.code === languageCode);
        setCurrentTranslatingText(`Переводим на ${language?.name || languageCode}: ${items.length} ключей`);
        
        // Prepare texts for batch translation
        const textsToTranslate: string[] = [];
        const validItems: Array<{ key: string; languageId: string; languageName: string; languageCode: string }> = [];
        
        items.forEach(item => {
          const translationData = translationKeysData.find(t => t.key === item.key);
          if (translationData) {
            // ВСЕГДА приоритет английскому языку - ищем по ID, а не по коду
            const englishLang = activeLanguages.find(lang => lang.code === 'en');
            if (englishLang && translationData.translations[englishLang.id]) {
              textsToTranslate.push(translationData.translations[englishLang.id]);
              validItems.push(item);
              return;
            }
            
            // Если нет английского, ищем другие латинские языки (избегаем китайский, арабский и другие)
            const preferredLanguages = ['ru'];
            for (const langCode of preferredLanguages) {
              const lang = activeLanguages.find(l => l.code === langCode);
              if (lang && translationData.translations[lang.id]) {
                console.log(`⚠️ Используем ${langCode} вместо английского для ключа: ${item.key}`);
                textsToTranslate.push(translationData.translations[lang.id]);
                validItems.push(item);
                return;
              }
            }
            
            // ВАЖНО: НЕ используем сам ключ для перевода!
            console.warn(`❌ Пропускаем ключ без подходящего исходного текста: ${item.key}`);
          } else {
            console.warn(`❌ Нет данных для ключа: ${item.key}`);
          }
        });
        
        // Если нет валидных текстов для перевода, пропускаем этот язык
        if (textsToTranslate.length === 0) {
          console.warn(`⚠️ Нет валидных текстов для перевода на ${language?.name || languageCode}`);
          continue;
        }

        try {
          // Validate source texts before translation
          const validation = aiTranslationService.validateSourceTexts(textsToTranslate);
          if (validation.warnings.length > 0) {
            console.warn('⚠️ Валидация исходных текстов:', validation.warnings);
            validation.warnings.forEach(warning => console.warn(warning));
          }
          
          // Show current batch being processed
          setCurrentTranslatingText(`Переводим на ${language?.name || languageCode}: "${textsToTranslate[0]}"${textsToTranslate.length > 1 ? ` и еще ${textsToTranslate.length - 1}` : ''}`);
          
          // Use bulk translation for better performance
          const bulkResult = await aiTranslationService.translateBulk({
            texts: textsToTranslate,
            targetLanguage: language?.name || languageCode, // Используем name из объекта Language
            targetLanguageCode: languageCode,
            sourceLanguage: 'English'
          });

          // Map results back to our format
          const currentBatchResults: TranslationResult[] = [];
          
          console.log(`📥 API ОТВЕТ: получено ${bulkResult.translations.length} переводов для ${validItems.length} ключей`);
          
          bulkResult.translations.forEach((aiResult, index) => {
            if (index < validItems.length) {
              const item = validItems[index];
              const result = {
                key: item.key,
                languageId: item.languageId,
                languageName: item.languageName,
                languageCode: item.languageCode,
                originalValue: textsToTranslate[index],
                translatedValue: aiResult.translatedText,
                success: true,
              };
              allResults.push(result);
              currentBatchResults.push(result);
              console.log(`✅ ДОБАВЛЕН РЕЗУЛЬТАТ #${allResults.length}: ${item.key} = "${aiResult.translatedText}"`);
            }
          });

          // Handle errors
          bulkResult.errors.forEach(error => {
            if (error.index < validItems.length) {
              const item = validItems[error.index];
              const result = {
                key: item.key,
                languageId: item.languageId,
                languageName: item.languageName,
                languageCode: item.languageCode,
                originalValue: '',
                translatedValue: '',
                success: false,
                error: error.error
              };
              allResults.push(result);
              currentBatchResults.push(result);
            }
          });

          console.log(`📊 ПЕРЕД ОБНОВЛЕНИЕМ: allResults.length = ${allResults.length}, currentBatchResults.length = ${currentBatchResults.length}`);
          
          // НЕМЕДЛЕННО обновляем прогресс после каждого API ответа
          setAiTranslateResults([...allResults]);
          setProcessedCount(allResults.length);
          
          console.log(`🚀 СОСТОЯНИЕ ОБНОВЛЕНО: setProcessedCount(${allResults.length}), процент = ${Math.round((allResults.length / keysToTranslate.length) * 100)}%`);
          
          // Show what was just translated
          if (currentBatchResults.length > 0) {
            const successfulTranslations = currentBatchResults.filter(r => r.success);
            if (successfulTranslations.length > 0) {
              const firstKey = successfulTranslations[0].key;
              setCurrentTranslatingText(`✅ Переведено: "${firstKey}"${successfulTranslations.length > 1 ? ` и еще ${successfulTranslations.length - 1}` : ''}`);
              
              // Show completed translations for 1.5 seconds
              await new Promise(resolve => setTimeout(resolve, 1500));
            }
          }

          // Add delay between language batches
          await new Promise(resolve => setTimeout(resolve, 500));
          
        } catch (error) {
          console.error(`Translation error for language ${languageCode}:`, error);
          
          // Mark all items for this language as failed
          items.forEach(item => {
            allResults.push({
              key: item.key,
              languageId: item.languageId,
              languageName: item.languageName,
              languageCode: item.languageCode,
              originalValue: '',
              translatedValue: '',
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          });
          
          // НЕМЕДЛЕННО обновляем прогресс даже при ошибке
          setAiTranslateResults([...allResults]);
          setProcessedCount(allResults.length);
          setCurrentTranslatingText(`❌ Ошибка при переводе на ${language?.name || languageCode}`);
          
          console.log(`💥 ОШИБКА, ПРОГРЕСС: ${allResults.length}/${keysToTranslate.length} = ${Math.round((allResults.length / keysToTranslate.length) * 100)}%`);
        }
      }

      setCurrentTranslatingText('');
      message.success(`Обработано ${allResults.length} переводов. Проверьте результаты.`);
    } catch (error) {
      console.error('AI Bulk translation error:', error);
      message.error('Ошибка при массовом переводе: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setAiTranslateLoading(false);
      setCurrentTranslatingText('');
    }
  };

  // Column visibility popover content
  const columnVisibilityContent = (
    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
      {activeLanguages.map(language => (
        <div key={language.id} style={{ padding: '4px 0' }}>
          <Checkbox
            checked={visibleLanguages.includes(language.id)}
            onChange={(e) => handleLanguageVisibilityChange(language.id, e.target.checked)}
          >
            {language.name} ({language.code})
          </Checkbox>
        </div>
      ))}
    </div>
  );

  // Table columns
  const columns = [
    {
      title: t('translationsManager.key'),
      dataIndex: 'key',
      key: 'key',
      width: 200,
      fixed: 'left' as const,
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {text}
        </Text>
      ),
    },
    ...visibleLanguages.map(languageId => {
      const language = activeLanguages.find(lang => lang.id === languageId);
      if (!language) return undefined;

      return {
        title: (
          <Space>
            <span>{language.name}</span>
            <Text type="secondary">({language.code})</Text>
          </Space>
        ),
        key: languageId,
        width: 250,
        render: (record: TranslationKey) => {
          if (!record || !record.key || !record.translations || typeof record.translations !== 'object') {
            return <Text type="secondary">—</Text>;
          }
          
          const value = record.translations[languageId] || '';
          const isEditing = editingCell?.key === record.key && editingCell?.languageId === languageId;
          const editKey = `${record.key}-${languageId}`;

          if (isEditing) {
            return (
              <Space.Compact style={{ width: '100%' }}>
                <TextArea
                  value={editValues[editKey]}
                  onChange={(e) => setEditValues(prev => ({
                    ...prev,
                    [editKey]: e.target.value
                  }))}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => handleEditSave(record.key, languageId)}
                  loading={updateTranslationMutation.isPending}
                />
                <Button
                  size="small"
                  icon={<ClearOutlined />}
                  onClick={handleEditCancel}
                />
              </Space.Compact>
            );
          }

          return (
            <div
              style={{
                minHeight: '32px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid transparent',
                backgroundColor: value ? '#f6ffed' : '#fff2f0',
              }}
              onClick={() => handleEditStart(record.key, languageId, value)}
            >
              {value ? (
                <Text style={{ fontSize: '12px' }}>{value}</Text>
              ) : (
                <Text type="secondary" italic style={{ fontSize: '12px' }}>
                  Нажмите для добавления перевода
                </Text>
              )}
              <EditOutlined 
                style={{ 
                  float: 'right', 
                  opacity: 0.6,
                  fontSize: '12px'
                }} 
              />
            </div>
          );
        },
      };
    }).filter((column): column is NonNullable<typeof column> => column !== undefined),
  ];

  if (languagesLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>{t('translationsManager.title')}</Title>
          <Text type="secondary">
            {t('translationsManager.description')}
          </Text>
        </Col>
        <Col>
          <Space>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              beforeUpload={handleImportFromExcel}
            >
              <Button
                icon={<UploadOutlined />}
                style={{
                  height: '40px',
                  borderRadius: '10px',
                }}
              >
                Импорт Excel
              </Button>
            </Upload>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportToExcel}
              style={{
                height: '40px',
                borderRadius: '10px',
              }}
            >
              Экспорт Excel
            </Button>
            <Button
              type="primary"
              className="ai-translate-btn"
              icon={<AIIcon loading={aiTranslateLoading} />}
              onClick={handleAIBulkTranslate}
              loading={aiTranslateLoading}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                border: 'none',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                height: '40px',
                padding: '0px 24px 0px 10px',
                fontSize: '15px',
                fontWeight: 500,
                borderRadius: '10px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {t('translationsManager.aiTranslation')}
            </Button>
            <style>{`
              .ai-translate-btn:hover:not(:disabled) {
                transform: translateY(-2px) scale(1.02);
                box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5) !important;
                background: linear-gradient(135deg, #764ba2 0%, #667eea 50%, #f093fb 100%) !important;
              }
              .ai-translate-btn:active:not(:disabled) {
                transform: translateY(0) scale(0.98);
              }
            `}</style>
          </Space>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* Project selection */}
          <Row justify="space-between" align="middle">
            <Col span={8}>
              <Space>
                <ProjectOutlined />
                <Text strong>{t('translationsManager.project')}</Text>
                <Select
                  value={selectedProjectId}
                  onChange={setSelectedProjectId}
                  placeholder={t('translationsManager.selectProject')}
                  style={{ width: 200 }}
                  loading={projectsLoading}
                >
                  {projectsData?.map(project => (
                    <Select.Option key={project.id} value={project.id}>
                      {project.displayName}
                    </Select.Option>
                  ))}
                </Select>
              </Space>
            </Col>
          </Row>

          {/* Filters and controls */}
          <Row justify="space-between" align="middle">
            <Col span={12}>
              <Input
                placeholder={t('translationsManager.searchPlaceholder')}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: '100%' }}
                allowClear
              />
            </Col>
            <Col>
              <Space>
                <Popover
                  content={columnVisibilityContent}
                  trigger="click"
                  placement="bottomRight"
                >
                  <Button icon={<ColumnHeightOutlined />}>
                    {t('translationsManager.visibleLanguages', { count: visibleLanguages.length })}
                  </Button>
                </Popover>
              </Space>
            </Col>
          </Row>

          {/* Statistics */}
          <Row gutter={16}>
            <Col span={6}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">{t('translationsManager.totalKeys')}</Text>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {allTranslationKeys.size}
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">{t('translationsManager.activeLanguages')}</Text>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {activeLanguages.length}
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">{t('translationsManager.visibleLanguagesCount')}</Text>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {visibleLanguages.length}
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">{t('translationsManager.found')}</Text>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {filteredTranslations.length}
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Bulk actions bar */}
          {selectedRowKeys.length > 0 && (
            <Card size="small" style={{ backgroundColor: '#e6f4ff', border: '1px solid #91caff' }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong>Выбрано ключей: {selectedRowKeys.length}</Text>
                </Col>
                <Col>
                  <Space>
                    <Button
                      icon={<AIIcon loading={aiTranslateLoading} />}
                      onClick={handleAIBulkTranslate}
                      loading={aiTranslateLoading}
                    >
                      AI перевод выбранных
                    </Button>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleBulkDelete}
                      loading={bulkDeleteMutation.isPending}
                    >
                      Удалить ключи
                    </Button>
                    <Button onClick={() => setSelectedRowKeys([])}>
                      Снять выделение
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>
          )}

          {/* Main table */}
          {!selectedProjectId ? (
            <Empty description={t('translationsManager.selectProjectMessage')} />
          ) : (languagesLoading || translationsLoading) ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>{
                languagesLoading ? t('translationsManager.loadingLanguages') : 
                translationsLoading ? t('translationsManager.loadingTranslations') : ''
              }</div>
            </div>
          ) : filteredTranslations.length === 0 ? (
            <Empty description={t('translationsManager.noTranslationsInProject')} />
          ) : (
            <Table
              columns={columns}
              dataSource={filteredTranslations.filter(item => item && item.key && item.translations)}
              rowKey="key"
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                selections: [
                  Table.SELECTION_ALL,
                  Table.SELECTION_INVERT,
                  Table.SELECTION_NONE,
                ],
                columnWidth: 48,
                fixed: true,
              }}
              loading={translationsLoading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: 1000 }}
            />
          )}
        </Space>
      </Card>
      {/* AI Translation Modal */}
      <BulkTranslateModal
        visible={aiTranslateModalVisible}
        onCancel={() => setAiTranslateModalVisible(false)}
        results={aiTranslateResults}
        isLoading={aiTranslateLoading}
        totalKeys={totalKeysToTranslate}
        currentTranslatingText={currentTranslatingText}
        processedCount={processedCount}
        onStartTranslation={handleStartAITranslation}
        selectedProjectId={selectedProjectId}
        onSaveTranslations={() => {
          // Invalidate project translations query
          queryClient.invalidateQueries({ 
            queryKey: ['projectTranslations', selectedProjectId] 
          });
        }}
      />
    </div>
  );
};

export default TranslationsManager;
